import { useState } from 'react';
import { Link } from 'react-router';
import SocialButtons, { SOCIAL_SIGN_ON_ENABLED } from '@ui/SocialButtons.jsx';

// The "Free Account Box" design (see the published Free Account Box
// artifact) rebuilt against this app's own Tailwind tokens (index.css's
// @theme - same ink-*/paper/brand names the artifact's own
// --ink-*/--paper/--brand CSS vars used) rather than the artifact's inline
// styles. Distinct from prototypes/signup.html (the "signup prototype"),
// which is a full standalone page - this renders *in place of* the trial
// box's own contents (routes/landing.jsx swaps it in, keeping that box's
// own outer card/header/close chrome) rather than as a separate modal, so
// it only ever renders this component's own grid content, not a full page
// or dialog shell of its own.
//
// Nothing is created here: this is the front door to the same wizard
// /sign-up renders (freeSignup/SignUpWizard.jsx), just entered with the
// name and email already filled in, so it hands those to `onContinue` and
// lets that wizard open on its second step. Keeping one wizard - rather
// than a second, parallel signup path that happens to start on the landing
// page - is why the account creation call lives there and not here.
const VALUE_POINTS = [
  ['Reads even the toughest handwriting', 'Even your hardest-to-read tech notes come back as a clean write-up.'],
  ['Scan on your phone', 'Snap a repair order on your phone, then pick it up at your desk.'],
  ['Your repair orders, all in one place', 'Every scan saved and organized — pick up right where you left off.']
];

export default function FreeAccountBox({ onContinue }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !email.includes('@')) {
      setError('Enter your name and a valid email address.');
      return;
    }
    setError(null);
    onContinue({ name: name.trim(), email: email.trim() });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[6fr_4fr] gap-5 items-start">
      {/* Value reminder */}
      <div className="flex flex-col gap-5 py-1 order-2 md:order-1">
        <p className="text-[16px] leading-relaxed text-ink-400">
          Turn repair orders into clean write-ups you can read straight to a customer.
        </p>
        <div className="flex flex-col gap-3.5">
          {VALUE_POINTS.map(([title, body]) => (
            <div key={title} className="flex items-start gap-3">
              <span className="shrink-0 w-[30px] h-[30px] rounded-full bg-brand/10 border border-brand/25 text-brand flex items-center justify-center">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <div>
                <h4 className="font-display font-semibold text-[14px] text-paper mb-0.5">{title}</h4>
                <p className="text-[14px] leading-relaxed text-ink-400">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Signup form */}
      <div className="order-1 md:order-2 bg-ink-950/40 border border-ink-800 rounded-xl p-5 flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <div>
            <label htmlFor="fab-name" className="block text-xs font-medium text-ink-400 mb-1.5">
              Name
            </label>
            <input
              id="fab-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="Alex Rivera"
              className="w-full px-3 py-2.5 bg-ink-900 border border-ink-700 rounded-lg text-paper text-sm placeholder-ink-500 focus:outline-none focus:border-brand transition-colors"
            />
          </div>
          <div>
            <label htmlFor="fab-email" className="block text-xs font-medium text-ink-400 mb-1.5">
              E-Mail Address
            </label>
            <input
              id="fab-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alex@yourshop.com"
              className="w-full px-3 py-2.5 bg-ink-900 border border-ink-700 rounded-lg text-paper text-sm placeholder-ink-500 focus:outline-none focus:border-brand transition-colors"
            />
          </div>

          {error && <p className="text-xs text-rose-400 bg-rose-950/40 border border-rose-800/50 rounded-lg px-3 py-2">{error}</p>}

          <button
            type="submit"
            className="w-full mt-0.5 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-brand text-ink-900 rounded-lg font-display font-semibold text-sm hover:bg-brand-600 transition-colors"
          >
            Create free account
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {SOCIAL_SIGN_ON_ENABLED && (
            <>
              <div className="flex items-center gap-3 my-0.5">
                <div className="flex-1 h-px bg-ink-800" />
                <span className="text-[11px] font-medium uppercase tracking-wider text-ink-500">or continue with</span>
                <div className="flex-1 h-px bg-ink-800" />
              </div>

              <SocialButtons />
            </>
          )}
        </form>

        <p className="mt-4 pt-4 border-t border-ink-800 text-center text-sm text-ink-500">
          Already a member?{' '}
          <Link to="/sign-in" className="text-brand hover:text-brand-600">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
